/**
 * Promise-based confirmation dialogs:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete 3 images?', tone: 'danger', confirmLabel: 'Delete' })) …
 *
 * Rendered by `<ConfirmHost />` (mounted once in the app root).
 */
import type { ReactNode } from 'react';
import { create } from 'zustand';

export interface ConfirmOptions {
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

interface ConfirmState {
  pending: PendingConfirm | null;
}

export const useConfirmStore = create<ConfirmState>()(() => ({ pending: null }));

/** Opens a confirmation dialog; resolves `true` on confirm, `false` on cancel. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  useConfirmStore.getState().pending?.resolve(false);
  return new Promise((resolve) => {
    useConfirmStore.setState({
      pending: {
        ...options,
        resolve: (confirmed) => {
          useConfirmStore.setState({ pending: null });
          resolve(confirmed);
        },
      },
    });
  });
}

export function useConfirm(): typeof confirm {
  return confirm;
}
